import { Grip, Calendar, LayoutDashboard, FileText, FileEdit, BookOpen, MessageSquare, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { WorkspaceSelectionDialog } from "@/components/WorkspaceSelectionDialog";
import { useIsMobile } from "@/hooks/use-mobile";

import { useUserAppPreferences } from "@/hooks/useUserAppPreferences";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useState, useEffect } from "react";

interface AppLauncherProps {
  organizationId?: string;
}

interface App {
  id: string;
  title: string;
  description: string;
  icon: any;
  href?: string;
  onClick?: () => void;
  color: string;
  disabled?: boolean;
  comingSoon?: boolean;
}

export function AppLauncher({ organizationId }: AppLauncherProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  
  const { preferences, saveAppOrder, isLoading } = useUserAppPreferences();
  const [apps, setApps] = useState<App[]>([]);
  const [workspaceDialogOpen, setWorkspaceDialogOpen] = useState(false);
  
  const queueParam = searchParams.get('queue');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const allApps: App[] = [
    {
      id: "getting-started",
      title: "Getting\nStarted",
      description: "Learn how to use QueueOS",
      icon: BookOpen,
      onClick: () => window.open('https://help.queueos.com/collection/getting-started', '_blank'),
      color: "bg-purple-500",
    },
    {
      id: "feedback",
      title: "Feedback",
      description: "Share your thoughts on QueueOS",
      icon: MessageSquare,
      onClick: () => window.open('https://queueos.com/forms/feedback-on-queueos', '_blank'),
      color: "bg-orange-500",
    },
    {
      id: "dashboard",
      title: "Dashboard",
      description: "Main dashboard and overview",
      icon: LayoutDashboard,
      href: "/dashboard",
      color: "bg-blue-500",
    },
    {
      id: "workspaces",
      title: "Workspaces",
      description: "Switch to a different workspace",
      icon: Building2,
      onClick: () => setWorkspaceDialogOpen(true),
      color: "bg-indigo-500",
    },
    {
      id: "documents",
      title: "Documents",
      description: "Create and edit rich text documents",
      icon: FileText,
      href: `/documents`,
      color: "bg-emerald-500",
    },
  ];


  // Sort apps based on user preferences
  useEffect(() => {
    if (isLoading) return;

    const { app_order } = preferences;
    
    // Default order: getting-started, feedback, dashboard, workspaces, documents
    const defaultOrder = ['getting-started', 'feedback', 'dashboard', 'workspaces', 'documents'];
    
    if (app_order.length === 0) {
      // No saved preferences, use default order
      const orderedApps = defaultOrder.map(id => allApps.find(app => app.id === id)).filter(Boolean) as App[];
      setApps(orderedApps);
      return;
    }

    // Sort apps according to user preferences, keeping default order for new apps
    const orderedApps = [...allApps].sort((a, b) => {
      const indexA = app_order.indexOf(a.id);
      const indexB = app_order.indexOf(b.id);
      
      // If both apps are not in preferences, use default order
      if (indexA === -1 && indexB === -1) {
        return defaultOrder.indexOf(a.id) - defaultOrder.indexOf(b.id);
      }
      // If only A is not in preferences, put it in default position
      if (indexA === -1) return defaultOrder.indexOf(a.id) - app_order.length;
      // If only B is not in preferences, put it in default position  
      if (indexB === -1) return app_order.length - defaultOrder.indexOf(b.id);
      
      return indexA - indexB;
    });

    setApps(orderedApps);
  }, [preferences, isLoading, user?.email, organizationId]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setApps((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const newOrder = arrayMove(items, oldIndex, newIndex);
        const newAppOrder = newOrder.map(app => app.id);
        saveAppOrder(newAppOrder);
        
        return newOrder;
      });
    }
  };

  const handleAppClick = (app: App) => {
    if (app.disabled) return;
    
    if (app.href) {
      const url = queueParam ? `${app.href}?queue=${queueParam}` : app.href;
      navigate(url);
    } else if (app.onClick) {
      app.onClick();
    }
  };

  const SortableAppTile = ({ app }: { app: App }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: app.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const handleClick = (e: React.MouseEvent) => {
      // Only handle click if not dragging
      if (!isDragging) {
        handleAppClick(app);
      }
    };
    const content = (
      <div 
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        className={`group relative p-4 rounded-xl border transition-all duration-200 hover:shadow-md select-none ${
          app.disabled 
            ? 'opacity-50 cursor-not-allowed bg-muted/50' 
            : 'cursor-pointer hover:border-primary/20 bg-background hover:bg-accent/5'
        }`}
        onClick={handleClick}
        role={app.disabled ? undefined : "button"}
        tabIndex={app.disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!app.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleAppClick(app);
          }
        }}
        aria-disabled={app.disabled}
      >
        <div className="flex flex-col items-center text-center space-y-2">
          <div className={`p-3 rounded-lg ${app.color} text-white`}>
            <app.icon className="h-6 w-6" />
          </div>
          <div>
            <div className="font-medium text-xs whitespace-pre-line">{app.title}</div>
            {app.comingSoon && isMobile && (
              <Badge variant="secondary" className="text-xs mt-1">
                Soon
              </Badge>
            )}
          </div>
        </div>
      </div>
    );

    if (app.comingSoon && !isMobile) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              {content}
            </TooltipTrigger>
            <TooltipContent>
              <p>Coming soon</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return content;
  };

  if (isLoading) {
    return (
      <Popover>
        <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9">
            <Grip className="h-4 w-4" />
            <span className="sr-only">App launcher</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent 
          className="w-80 p-4 bg-popover border-border shadow-lg z-50" 
          align="end"
          sideOffset={8}
        >
          <div className="space-y-3">
            <h3 className="font-semibold text-sm text-muted-foreground">Loading apps...</h3>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-9 w-9">
          <Grip className="h-4 w-4" />
          <span className="sr-only">App launcher</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-4 bg-popover border-border shadow-lg z-50" 
        align="end"
        sideOffset={8}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm text-muted-foreground">Apps</h3>
            <p className="text-xs text-muted-foreground">Hold to reorder</p>
          </div>
          <DndContext 
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={apps.map(app => app.id)} strategy={verticalListSortingStrategy}>
              <div className="grid grid-cols-3 gap-3">
                {apps.map((app) => (
                  <SortableAppTile key={app.id} app={app} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </PopoverContent>
      <WorkspaceSelectionDialog 
        open={workspaceDialogOpen} 
        onOpenChange={setWorkspaceDialogOpen} 
      />
    </Popover>
  );
}